import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import {
  CardBodyComponent,
  CardComponent,
  GroundworksTableComponent,
  GroundworksTableData,
  IMAGEKIT_THUMBNAIL_CONFIG,
  ImageTile,
  ImageTileComponent,
  MenuOption,
  PaginationComponent,
  StateMessageComponent,
  TableGroup,
  toImageKitThumbnailUrl,
} from '@groundworks/ngx-groundworks-ui';
import { Gallery, GalleryItem, ImageItem } from 'ng-gallery';
import { Lightbox, LightboxModule } from 'ng-gallery/lightbox';
import { AttachmentSectionDataConstants } from '../../constants/attachment-section-data.constants';
import { getUserRoles } from '../../models/roles.model';
import { AttachmentInfo, AttachmentSectionInfo } from '../../sdk/sdk';
import { AttachmentService } from '../../services/attachment.service';
import { FileDownloadService } from '../../services/file-download.service';
import { FileUtilityService } from '../../services/file-utility.service';
import { ModalService } from '../../services/modal.service';
import { pageCount, pageRangeLabel, pageSlice } from '../../utils/paginate';

/** Attachments shown per page in each viewer tab (MOD-5537). Change it here only. */
export const ATTACHMENTS_PAGE_SIZE = 50;

type TabType = 'pictures' | 'documents';
type ViewMode = 'grid' | 'list';

@Component({
  selector: 'app-attachments',
  imports: [
    CardComponent,
    CardBodyComponent,
    FormsModule,
    GroundworksTableComponent,
    ImageTileComponent,
    LightboxModule,
    PaginationComponent,
    StateMessageComponent,
  ],
  templateUrl: './attachments.component.html',
  styleUrl: './attachments.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachmentsComponent implements OnInit {
  private readonly attachmentService = inject(AttachmentService);
  private readonly fileDownloadService = inject(FileDownloadService);
  private readonly fileUtilityService = inject(FileUtilityService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly gallery = inject(Gallery);
  private readonly lightbox = inject(Lightbox);
  private readonly thumbnailConfig = inject(IMAGEKIT_THUMBNAIL_CONFIG);

  readonly opportunityId = input.required<string>();

  readonly attachments = signal<AttachmentInfo[]>([]);
  readonly isLoading = signal(false);
  readonly hasError = signal(false);
  readonly isCollapsed = signal(false);
  readonly activeTab = signal<TabType>('documents');
  readonly viewMode = signal<ViewMode>('grid');
  readonly selectedSection = signal<string | null>(AttachmentSectionDataConstants.Production);

  readonly availableSections = signal<AttachmentSectionInfo[]>([]);

  readonly sectionFilteredAttachments = computed(() => {
    const section = this.selectedSection();
    if (section === null) return this.attachments();
    return this.attachments().filter((a) => a.attachmentSectionId === section);
  });

  readonly pictures = computed(() =>
    this.sectionFilteredAttachments().filter((a) => this.fileUtilityService.isImageFile(a))
  );

  readonly documents = computed(() =>
    this.sectionFilteredAttachments().filter((a) => !this.fileUtilityService.isImageFile(a))
  );

  readonly picturesCount = computed(() => this.pictures().length);
  readonly documentsCount = computed(() => this.documents().length);

  readonly state = computed(() => {
    if (this.isLoading()) return 'loading';
    if (this.hasError()) return 'error';

    const items =
      this.activeTab() === 'pictures' ? this.pictures() : this.documents();
    return items.length === 0 ? 'no-matches' : 'matches';
  });

  readonly currentItems = computed(() =>
    this.activeTab() === 'pictures' ? this.pictures() : this.documents()
  );

  readonly currentPage = signal(1);

  readonly totalPages = computed(() => pageCount(this.currentItems().length, ATTACHMENTS_PAGE_SIZE));

  // Derived rather than written back, so a delete that empties the last page needs no extra code.
  readonly effectivePage = computed(() => Math.min(this.currentPage(), this.totalPages()));

  readonly pagedItems = computed(() =>
    pageSlice(this.currentItems(), this.effectivePage(), ATTACHMENTS_PAGE_SIZE)
  );

  readonly rangeLabel = computed(() =>
    pageRangeLabel(this.currentItems().length, this.effectivePage(), ATTACHMENTS_PAGE_SIZE)
  );

  private readonly attachmentsScroll = viewChild<ElementRef<HTMLElement>>('attachmentsScroll');

  onPageChange(page: number): void {
    this.currentPage.set(page);
    // The tiles scroll inside their own window; start each page at its first tile.
    this.attachmentsScroll()?.nativeElement.scrollTo({ top: 0 });
  }

  readonly attachmentsListTableData = computed((): GroundworksTableData => {
    const items = this.pagedItems();
    const showSectionColumn = this.selectedSection() === null;
    const columns: GroundworksTableData['columns'] = [
      { label: 'Description', dataType: 'text' },
      { label: 'File name', dataType: 'text' },
      ...(showSectionColumn ? [{ label: 'Attachment section', dataType: 'text' as const }] : []),
      { label: 'Group', dataType: 'text' },
      { label: 'Uploaded', dataType: 'date' },
      { label: 'Action', dataType: 'action-menu', width: '0' },
    ];
    const rows: TableGroup['rows'] = items.map((attachment) => ({
      type: 'data' as const,
      cells: [
        { value: attachment.description ?? '--' },
        { value: attachment.fileName ?? '', action: () => this.onTileClick(attachment) },
        ...(showSectionColumn
          ? [{ value: attachment.attachmentSectionName ?? '--' }]
          : []),
        { value: attachment.attachmentGroupName ?? '--' },
        {
          value: attachment.createdAt
            ? new Date(attachment.createdAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
            : '--',
        },
        {
          value: '',
          actionMenu: {
            rowTitle: attachment.fileName ?? 'Attachment',
            actions: [
              ...this.getMenuOptions(attachment).map((opt) => ({
                label: opt.label,
                action: opt.action,
              })),
            ],
          },
        },
      ],
    }));
    return { columns, groups: [{ label: '', rows }] };
  });

  ngOnInit(): void {
    this.loadAttachments();
    this.loadSections();
  }

  private loadSections(): void {
    this.attachmentService
      .getAttachmentSections()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((sections) => this.availableSections.set(sections));
  }

  toggleCollapsed(): void {
    this.isCollapsed.update((v) => !v);
  }

  selectTab(tab: TabType): void {
    this.activeTab.set(tab);
    this.currentPage.set(1);
  }

  selectSection(section: string | null): void {
    this.selectedSection.set(section);
    this.currentPage.set(1);
  }

  setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
  }

  isImageFile(attachment: AttachmentInfo): boolean {
    return this.fileUtilityService.isImageFile(attachment);
  }

  toImageTile(attachment: AttachmentInfo): ImageTile {
    return {
      title: attachment.attachmentGroupName ?? 'No Group Selected',
      description: attachment.description ?? 'No Description Available',
      fileUrl: attachment.filePath!,
      timestamp: attachment.createdAt,
      type: this.fileUtilityService.getFileType(attachment),
      hasFlag: false,
      thumbnailUrl: attachment.filePath!,
      menuOptions: this.getMenuOptions(attachment),
    };
  }

  onTileClick(attachment: AttachmentInfo): void {
    if (this.fileUtilityService.isImageFile(attachment)) {
      this.openLightbox(attachment);
    } else {
      window.open(attachment.filePath ?? '', '_blank');
    }
  }

  private getMenuOptions(attachment: AttachmentInfo): MenuOption[] {
    const options: MenuOption[] = [
      {
        label: 'Download',
        action: () => this.downloadAttachment(attachment),
      },
    ];

    const roles = getUserRoles();
    const isProductionSection = attachment.attachmentSectionId === AttachmentSectionDataConstants.Production;
    const canEdit = roles.SuperUser || (roles.ProductionAdmin && isProductionSection);

    if (canEdit) {
      options.push({
        label: 'Edit',
        action: () => this.editAttachment(attachment),
      }, {
        label: 'Delete',
        action: () => this.deleteAttachment(attachment),
      });
    }

    return options;
  }

  private downloadAttachment(attachment: AttachmentInfo): void {
    this.fileDownloadService.download(
      attachment.filePath ?? '',
      attachment.fileName ?? 'download'
    );
  }

  private editAttachment(attachment: AttachmentInfo): void {
    const modalRef = this.modalService.openAttachmentEditModal(
      attachment.id,
      attachment.attachmentGroupId ?? undefined,
      attachment.description ?? ''
    );

    modalRef.result
      .then((updated: AttachmentInfo) => {
        if (updated) {
          this.attachments.update((list) =>
            list.map((a) => (a.id === updated.id ? updated : a))
          );
        }
      })
      .catch(() => {
        // Modal dismissed, no action needed
      });
  }

  private deleteAttachment(attachment: AttachmentInfo): void {
    const modalRef = this.modalService.openConfirmModal(
      'Delete Attachment',
      `Are you sure you want to delete "${attachment.fileName}"?`,
      'Delete',
      'alert-danger'
    );

    modalRef.result
      .then((confirmed: boolean) => {
        if (confirmed) {
          this.performDelete(attachment);
        }
      })
      .catch(() => {
        // Modal dismissed, no action needed
      });
  }

  private performDelete(attachment: AttachmentInfo): void {
    this.attachmentService
      .deleteAttachment(attachment.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.attachments.update((list) =>
            list.filter((a) => a.id !== attachment.id)
          );
        },
        error: (error) => {
          console.error('Error deleting attachment:', error);
          this.modalService.openErrorModal(
            'Error Deleting Attachment',
            this.getErrorMessage(
              error,
              'An unexpected error occurred while deleting the attachment. Please try again.'
            )
          );
        },
      });
  }

  private openLightbox(attachment: AttachmentInfo): void {
    // Every picture, not just the current page: with the thumbnail strip off and lazy loading,
    // ng-gallery only downloads the slide on screen, so stepping past a page boundary is free.
    const pics = this.pictures();
    const items: GalleryItem[] = pics.map(
      (p) =>
        new ImageItem({
          src: p.filePath ?? '',
          thumb: toImageKitThumbnailUrl(p.filePath ?? '', this.thumbnailConfig),
        })
    );

    const galleryRef = this.gallery.ref('attachments-lightbox', {
      counter: true,
      loadingStrategy: 'lazy',
      loadingAttr: 'lazy',
    });
    galleryRef.load(items);

    const index = pics.findIndex((p) => p.id === attachment.id);
    this.lightbox.open(Math.max(index, 0), 'attachments-lightbox');
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    const err = error as {
      status?: number;
      error?: { detail?: string };
      message?: string;
    };
    const detail = err?.error?.detail;
    switch (err?.status) {
      case 403:
        return 'The current logged in user is not allowed to perform this action.';
      case 404:
        return detail || 'The requested resource could not be found.';
      case 500:
        return detail || 'An internal server error occurred.';
      default:
        return detail || err?.message || fallback;
    }
  }

  onAddAttachment(): void {
    const opportunityId = this.opportunityId();
    if (!opportunityId) {
      console.error('Opportunity ID is required to upload attachments.');
      return;
    }

    const fileModalRef = this.modalService.openAttachmentUploadModal();

    fileModalRef.result
      .then((files: File[] | undefined) => {
        if (!files || files.length === 0) {
          return;
        }
        const detailsModalRef =
          this.modalService.openAttachmentUploadDetailsModal(files, opportunityId);

        detailsModalRef.result
          .then((result: { reload?: boolean } | void) => {
            if (result === undefined || result?.reload === true) {
              this.loadAttachments();
            }
          })
          .catch(() => undefined);
      })
      .catch(() => undefined);
  }

  loadAttachments(): void {
    this.currentPage.set(1);
    this.isLoading.set(true);
    this.hasError.set(false);

    this.attachmentService
      .getAttachmentsByOpportunityId(this.opportunityId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (attachments) => {
          this.attachments.set(attachments);
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error loading attachments:', error);
          this.hasError.set(true);
          this.isLoading.set(false);
          this.modalService.openErrorModal(
            'Error Loading Attachments',
            this.getErrorMessage(
              error,
              'An unexpected error occurred while loading attachments. Please try again.'
            )
          );
        },
      });
  }
}
